import { useCallback, useRef, useState } from "react";
import { defineMessages } from "react-intl";

import { withPortal } from "../GeneralServices/PortalService";
import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import AuthenticationPasswordForm from "./AuthenticationPasswordForm";
import { getDisplayName } from "../Utils/getDisplayName";
import { useFormatMessage } from "../Intl/Context";

function withPasswordForm(WrappedComponent) {
  const WithPasswordForm = props => {
    const { showPortal, hidePortal, onAlertMessageClicked } = props;

    const [passwordFormShown, setPasswordFormShown] = useState(false);

    const t = useFormatMessage();
    const forgotPasswordError = t(intlMessages.forgotPasswordError, {
      a: message => `<a href="#reset-password">${message}</a>`
    });

    const { showAlert, hideAlert } = useAlert();

    // Declare the `showPasswordForm` and `hidePasswordForm` variables here to be used in
    // the callbacks before they're assigned
    let showPasswordForm, hidePasswordForm;

    const handleAlertMessageClicked = useCallback(
      e => {
        if (e.target.href && e.target.href.match(/#reset-password$/)) {
          e.preventDefault();
          e.stopPropagation();

          showPasswordForm();
          hideAlert();
        } else if (onAlertMessageClicked) {
          // Allow chaining onAlertMessageClicked with other components
          onAlertMessageClicked(e);
        }
      },
      [hideAlert, onAlertMessageClicked, showPasswordForm]
    );

    const handlePasswordSendClose = useCallback(
      (success, message) => {
        hidePasswordForm();

        if (message) {
          showAlert({
            message,
            type: success ? ALERT_TYPE_SUCCESS : ALERT_TYPE_ERROR
          });
        }
      },
      [hidePasswordForm, showAlert]
    );

    const portalIndexRef = useRef();

    showPasswordForm = useCallback(
      email => {
        portalIndexRef.current = showPortal(
          <AuthenticationPasswordForm email={email} onClose={handlePasswordSendClose} />
        );

        setPasswordFormShown(true);
      },
      [handlePasswordSendClose, showPortal]
    );

    hidePasswordForm = useCallback(() => {
      if (portalIndexRef.current) {
        hidePortal(portalIndexRef.current);
        portalIndexRef.current = null;

        setPasswordFormShown(false);
      }
    }, [hidePortal]);

    return (
      <WrappedComponent
        {...props}
        key="wrapped-component"
        onAlertMessageClicked={handleAlertMessageClicked}
        showPasswordForm={showPasswordForm}
        hidePasswordForm={hidePasswordForm}
        forgotPasswordErrorMessage={forgotPasswordError}
        // The below prop is needed to force rerendering of the
        // WrappedComponent when the parent's component state is updated
        passwordFormShown={passwordFormShown}
      />
    );
  };

  WithPasswordForm.displayName = `WithPasswordForm(${getDisplayName(WrappedComponent)})`;

  return withPortal(WithPasswordForm);
}

export { withPasswordForm };

const intlMessages = defineMessages({
  forgotPasswordError: {
    id: "authentication.signIn.forgotPasswordError",
    defaultMessage: `
      Impossible de vous connecter. L'identifiant et le mot de passe
      saisis ne correspondent pas ou n'existent pas. Si vous ne vous
      souvenez pas de votre mot de passe, vous pouvez utiliser le lien
      suivant pour le réinitialiser : <a>Réinitialiser mon mot de passe</a>.
    `
  }
});
